/**
 * 客户端诊断工具
 * 连接/性能/配置自检报告
 */
export interface DiagnosticReport {
  timestamp: number
  connection: {
    status: string
    latency: number
    reconnects: number
    uptime: number
  }
  system: {
    nodeVersion: string
    platform: string
    memoryUsage: { heapUsed: number; heapTotal: number; rss: number }
    cpuUsage: { user: number; system: number }
  }
  config: {
    valid: boolean
    issues: string[]
  }
  handlers: {
    registered: string[]
    count: number
  }
}

export interface DiagnosticOptions {
  /** 检查项 */
  checks?: Array<'connection' | 'system' | 'config' | 'handlers'>
}

export class ClientDiagnostic {
  private customChecks: Array<{ name: string; check: () => any }> = []

  /** 添加自定义检查 */
  addCheck(name: string, check: () => any): void {
    this.customChecks.push({ name, check })
  }

  /** 运行诊断 */
  run(context: {
    connectionStatus?: string
    latency?: number
    reconnects?: number
    uptime?: number
    handlers?: string[]
    configIssues?: string[]
  }): DiagnosticReport {
    const mem = process.memoryUsage()
    const cpu = process.cpuUsage()

    return {
      timestamp: Date.now(),
      connection: {
        status: context.connectionStatus ?? 'unknown',
        latency: context.latency ?? 0,
        reconnects: context.reconnects ?? 0,
        uptime: context.uptime ?? 0,
      },
      system: {
        nodeVersion: process.version,
        platform: process.platform,
        memoryUsage: { heapUsed: mem.heapUsed, heapTotal: mem.heapTotal, rss: mem.rss },
        cpuUsage: { user: cpu.user, system: cpu.system },
      },
      config: {
        valid: !context.configIssues || context.configIssues.length === 0,
        issues: context.configIssues ?? [],
      },
      handlers: {
        registered: context.handlers ?? [],
        count: context.handlers?.length ?? 0,
      },
    }
  }

  /** 运行自定义检查 */
  runCustomChecks(): Record<string, any> {
    const results: Record<string, any> = {}
    for (const { name, check } of this.customChecks) {
      try { results[name] = check() }
      catch (err: any) { results[name] = { error: err.message } }
    }
    return results
  }

  /** 快速健康检查 */
  quickCheck(connectionStatus: string): { healthy: boolean; issues: string[] } {
    const issues: string[] = []
    if (connectionStatus !== 'connected') issues.push('未连接')
    const mem = process.memoryUsage()
    if (mem.heapUsed / mem.heapTotal > 0.9) issues.push('内存使用过高')
    return { healthy: issues.length === 0, issues }
  }
}
